import { Button, Dialog, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { post_Payment } from '../../../services/Payment'
import { branchTimezone } from '../../../utils/summaryCalculation'
import useAuth from '../../../components/hooks/useAuth'
import CustomFormatDatePicker from '../../../components/common/CustomFormatDatePicker'
import { useTheme } from '../../../components/hooks/useTheme'
import { validateMin } from '../../../utils/validateMin'
import useConfirm from '../../../components/hooks/useConfirm'
import colorConfigs from '../../../configs/colorConfigs'
import Cookies from 'js-cookie'
import useTenant from '../../../components/hooks/useTenant'
import moment from 'moment'

type AddOfflinePaymentProps = {
    open: boolean;
    onClose: () => void;
    isCompletedOrCancelled: boolean;
    setPleaseWait: (value: boolean) => void;
    reservationId: number;
    summaryData: any;
    formattedCreatedAt: string;
    refreshTheSummary: () => void;
    refreshThepayment: () => void;
    preferredDateFormat: string;
    securityDepositAmount: number;
}

const AddOfflinePayment = ({
    open,
    onClose,
    isCompletedOrCancelled,
    setPleaseWait,
    reservationId,
    summaryData,
    formattedCreatedAt,
    refreshTheSummary,
    refreshThepayment,
    preferredDateFormat,
    securityDepositAmount,
}: AddOfflinePaymentProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [isSendConfirmationEmail, setIsSendConfirmationEmail] = useState<boolean>(false);
    const [paymentType, setPaymentType] = useState<string>("Payment");
    const [paymentMethod, setPaymentMethod] = useState<string>("Bank Transfer");
    const [offlinePaymentDate, setOfflinePaymentDate] = useState<string>("");
    const [offlinePaymentAmount, setOfflinePaymentAmount] = useState<number>(0);
    const [offlineReference, setOfflineReference] = useState<string>("");
    const [offlinePaymentStatus, setOfflinePaymentStatus] = useState<string>("Approved");
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    const UpdateTimezone = branchTimezone(formattedCreatedAt);

    useEffect(() => {
        if (open) {
            setOfflinePaymentDate(moment(new Date(UpdateTimezone)).format("YYYY-MM-DD"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (open && summaryData) {
            setOfflinePaymentAmount(summaryData?.outstandingBalance);
            setPaymentType("Payment");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, summaryData])

    const handleOfflinePayment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isCompletedOrCancelled) {
            setPleaseWait(true);


            if (paymentType === "Payment") {

                let outstandingPayment = summaryData?.outstandingBalance || 0;

                if (Number(offlinePaymentAmount) > outstandingPayment) {
                    setConfirmData({
                        title: 'Warning',
                        summary: "Payment amount cannot exceed the outstanding balance.",
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    setPleaseWait(false);
                    return;
                }
            }

            if (paymentType === "Security Deposit") {

                let securityDepositAmount = 0;
                securityDepositAmount = summaryData?.securityDepositAmount - summaryData?.securityDepositAmountPaid;

                if (Number(offlinePaymentAmount) > securityDepositAmount) {
                    setConfirmData({
                        title: 'Warning',
                        summary: "Payment amount cannot exceed the security deposit amount to pay.",
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    setPleaseWait(false);
                    return;
                }
            }

            if (Number(offlinePaymentAmount) <= 0) {
                setConfirmData({
                    title: 'Warning',
                    summary: paymentType === "Security Deposit"
                        ? "Security Deposit amount must be greater than zero."
                        : "Payment amount must be greater than zero.",
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
                setPleaseWait(false);
                return;
            }

            await post_Payment({
                reservationId: reservationId,
                paymentType: paymentType,
                paymentMethod: paymentMethod,
                dateofPayment: offlinePaymentDate,
                amount: offlinePaymentAmount,
                reference: offlineReference,
                status: offlinePaymentStatus,
                createdAt: new Date(UpdateTimezone).toLocaleString(),
                createdBy: auth.givenname,
                syncedWithAccounting: false,
                creditCardId: 0
            }, auth?.id, tenantId)
                .then(() => {
                    setIsSendConfirmationEmail(false);
                    setPaymentType("Payment");
                    setPaymentMethod("Bank Transfer");
                    setOfflinePaymentDate("");
                    setOfflinePaymentAmount(0);
                    setOfflineReference("");
                    setOfflinePaymentStatus("Approved");
                    refreshTheSummary();
                    refreshThepayment();
                    onClose();
                    handleTenantError(null);
                })
                .catch((error) => {
                    if (error?.response?.data?.includes("Declined")) {                    
                        refreshThepayment();
                    }  

                    setPleaseWait(false);
                    setConfirmData({
                        title: 'Warning',
                        summary: error.response.data,
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    handleTenantError(error);
                });
        }
    }


    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullScreen={false}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%',
                    borderRadius: '16px',
                    overflow: "unset"
                },
            }}
        >
            <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320.591 320.591"
                    className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                    onClick={() => onClose()}
                >
                    <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                    <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                </svg>
                <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                    <div className="w-full mt-2">
                        <div className={`w-full`}>
                            <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                <div className="flex items-center gap-1.5">
                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Payment</h2>
                                </div>
                            </div>

                            <div className="mt-2 p-2">
                                <div className="w-full">
                                    <form onSubmit={handleOfflinePayment}>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Payment Type</label>
                                                    <RadioGroup
                                                        row
                                                        value={paymentType}
                                                        onChange={(e) => {
                                                            if (e.target.value === "Payment") {
                                                                setOfflinePaymentAmount(summaryData?.outstandingBalance);
                                                            } else {
                                                                setOfflinePaymentAmount(securityDepositAmount);
                                                            }
                                                            setPaymentType(e.target.value);
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            value="Payment"
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: "gray",
                                                                        "&.Mui-checked": {
                                                                            color: "#167AC3",
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: 14,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label="Payment"
                                                            sx={{
                                                                color: themeStyles.form.labelTextColor,
                                                                fontSize: "12px"
                                                            }}
                                                        />

                                                        <FormControlLabel
                                                            value="Security Deposit"
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: "gray",
                                                                        "&.Mui-checked": {
                                                                            color: "#167AC3",
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: 14,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label="Authorization"
                                                            sx={{
                                                                color: themeStyles.form.labelTextColor,
                                                            }}
                                                        />
                                                    </RadioGroup>
                                                </div>
                                            </div>

                                            <div />
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Payment Method</label>
                                                    <select
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={paymentMethod}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                        required
                                                    >
                                                        <option className={`${themeStyles.main.tailwindSectionBg}`} value="Bank Transfer">Bank Transfer</option>
                                                        <option className={`${themeStyles.main.tailwindSectionBg}`} value="Cash">Cash</option>
                                                        <option className={`${themeStyles.main.tailwindSectionBg}`} value="Check">Check</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                                                    {
                                                        paymentType === "Payment"
                                                            ? (
                                                                <input
                                                                    type="number"
                                                                    step={0.01}
                                                                    min={0}
                                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                    value={offlinePaymentAmount}
                                                                    onChange={(e) =>
                                                                        setOfflinePaymentAmount(validateMin(e.target.value, 0))
                                                                    }
                                                                    required
                                                                />
                                                            ) : (
                                                                <div className="-mt-[0.125rem]">
                                                                    <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>${securityDepositAmount?.toFixed(2) || 0}</span>
                                                                </div>
                                                            )
                                                    }
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Payment Status</label>
                                                    <select
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={offlinePaymentStatus}
                                                        onChange={(e) =>
                                                            setOfflinePaymentStatus(e.target.value)
                                                        }
                                                        required
                                                    >
                                                        <option className={`${themeStyles.main.tailwindSectionBg}`} value="Approved">Approved</option>
                                                        <option className={`${themeStyles.main.tailwindSectionBg}`} value="Denied">Denied</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Send Confirmation Email</label>
                                                    <RadioGroup
                                                        row
                                                        value={isSendConfirmationEmail ? "Yes" : "No"}
                                                        onChange={(e) =>
                                                            setIsSendConfirmationEmail(
                                                                e.target.value === "Yes" ? true : false
                                                            )
                                                        }
                                                    >
                                                        <FormControlLabel
                                                            value="Yes"
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: "gray",
                                                                        "&.Mui-checked": {
                                                                            color: "#167AC3",
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: 14,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label="Yes"
                                                            sx={{
                                                                color: themeStyles.form.labelTextColor,
                                                                fontSize: "12px"
                                                            }}
                                                        />

                                                        <FormControlLabel
                                                            value="No"
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: "gray",
                                                                        "&.Mui-checked": {
                                                                            color: "#167AC3",
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: 14,
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label="No"
                                                            sx={{
                                                                color: themeStyles.form.labelTextColor,
                                                            }}
                                                        />
                                                    </RadioGroup>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Date</label>
                                                    <CustomFormatDatePicker
                                                        value={offlinePaymentDate}
                                                        onChange={setOfflinePaymentDate}
                                                        dateFormat={preferredDateFormat}
                                                        required={true}
                                                    />
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reference</label>
                                                    <input
                                                        type="text"
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={offlineReference}
                                                        onChange={(e) => setOfflineReference(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-4 mt-6">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.main.bg
                                                }}
                                                type="submit"
                                            >
                                                Save
                                            </Button>

                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </Button>


                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default AddOfflinePayment
